import { currencyLocales } from "./currencyLocales"

import styles from "./Price.module.css"

const getCurrencyFormat = (currencyCode: string): Intl.NumberFormat => {
  const matchedLocale = currencyCode
    ? currencyLocales[currencyCode]
    : currencyLocales.USD

  return new Intl.NumberFormat(matchedLocale?.locale ?? "en-US", {
    style: "currency",
    currency: matchedLocale ? currencyCode : "USD",
    currencyDisplay: "symbol",
    minimumFractionDigits: matchedLocale?.fractionDigits ?? 2,
    maximumFractionDigits: matchedLocale?.fractionDigits ?? 2,
  })
}

export type PriceTextProps = {
  amount: string | number
  currencyCode?: string
  showCurrencyCode?: boolean
}

export const PriceText = ({
  amount,
  currencyCode,
  showCurrencyCode = true,
}: PriceTextProps) => {
  const currencyFormat = getCurrencyFormat(currencyCode)
  return (
    <>
      {currencyFormat.format(
        typeof amount === "string" ? parseFloat(amount) : amount,
      )}{" "}
      {showCurrencyCode && currencyCode}
    </>
  )
}

export const priceSizes = ["sm", "lg"]

export const priceVariants = ["default", "featured"]

export type PriceProps = {
  amount: string | number
  currencyCode?: string
  showCurrencyCode?: boolean
  compareAtAmount?: string | number
  size?: (typeof priceSizes)[number]
  variant?: (typeof priceVariants)[number]
}

export const Price = ({
  amount,
  currencyCode,
  showCurrencyCode = true,
  compareAtAmount,
  size = "lg",
  variant = "default",
}: PriceProps) => {
  if (
    amount === undefined ||
    Number.isNaN(amount) ||
    Number.isNaN(compareAtAmount)
  ) {
    return null
  }

  const currencyFormat = getCurrencyFormat(currencyCode)

  return (
    <span
      className={`${styles.price} ${
        size === "sm" ? "body-md-strong" : "title-md"
      }`}
      data-size={size}
    >
      {!!compareAtAmount && (
        <span className={styles.compareAtPriceAmount}>
          {currencyFormat.format(
            typeof compareAtAmount === "string"
              ? parseFloat(compareAtAmount)
              : compareAtAmount,
          )}
        </span>
      )}
      <span
        className={
          variant === "featured"
            ? "text-color-primary"
            : "text-color-secondary"
        }
        data-compare-at={!!compareAtAmount}
      >
        <PriceText
          amount={amount}
          currencyCode={currencyCode}
          showCurrencyCode={showCurrencyCode}
        />
      </span>
    </span>
  )
}
